import i18n from "@app/dashboard/tools/i18n";
import type { ICafDetail } from "@app/dashboard/types/innerLayout/transactions";
import { formatAmountWithCommas } from "@unified-checkout/design-system";
import { convertAmountToCents, convertAmountToFixedDigits, convertCentsToMajorUnit } from "@app/dashboard/utils/convertAmounts";
import { getMinimumValidAmount } from "@app/dashboard/utils/getMinimumValidAmount";

export const MID_TAKSEET = "MID_TAKSEET";
export const HALAN = "halan";
export const INSTAPAY = "instapay";
export const OMANNET = "omannet";
export const GET_GO = "get_go";
export const SHAHRY = "shahry";
export const VALU = "valu";
export const AANI = "Aani";
const EMPTY_VALUE = '-';
const EXTERNAL_REFUND_METHODS = ['MADFU', 'KnetCard', 'KNET_APPLE_PAY'];

const SOUHOOLA_MIN_REFUND_VALUE = 500;
const CANCEL_TIME_LIMIT = 120;
const MS_IN_MIN = 1000 * 60;

const { t } = i18n;

const isInvalidZeroValue = (value: string) => {
  return /^0(?!\.)/.test(value);
};

export const calculateInstantRefundFee = (cafDetails: ICafDetail) => {
  return Array.isArray(cafDetails) ? cafDetails.reduce(
    (acc, item) => acc + item.caf_cents,
    0
  ) : 0;
}

export const maxRefundAmount = (selectedTransaction: any) => {
  const { amount_cents, refunded_amount_cents, source_data, installment_info, currency, caf_details } = selectedTransaction || {};

  const amount = convertCentsToMajorUnit(amount_cents, currency);
  const refundedAmount = convertCentsToMajorUnit(refunded_amount_cents, currency);

  const cafAmount = source_data?.instant_refund
    ? convertCentsToMajorUnit(calculateInstantRefundFee(caf_details) || 0, currency)
    : caf_details &&
      caf_details.refund_caf_amount === false &&
      caf_details.convenience_fee_amount
      ? convertCentsToMajorUnit(caf_details.convenience_fee_amount, currency)
      : 0;


  const remainingAmount = amount - refundedAmount;
  const downpayment = source_data?.type === MID_TAKSEET ? installment_info.down_payment : 0;
  const discountAmount = source_data?.type === VALU ? installment_info.discount_amount : 0;
  const maxRefundValue = remainingAmount - (downpayment + discountAmount + cafAmount);

  return maxRefundValue > 0 ? (Number(formatAmountWithCommas({ amount: maxRefundValue, currency })) || maxRefundValue) : 0;
};

export const isSouhoola = (type: string) => {
  return type.toLowerCase().includes("souhoolav3_online");
};

export const isKnet = (type: string) => {
  const lowerCaseType = type.toLowerCase();
  return (
    lowerCaseType.includes("knetcard") ||
    lowerCaseType.includes("knet-apple-pay")
  );
};

export const handleEmptyProductsState = (selectedTransaction: any) => {
  const { data } = selectedTransaction || {};
  const { invoice_details } = data || {};

  const noRefundProducts = !invoice_details;
  const allProductsRefunded = isAllProductsRefunded(invoice_details);

  return noRefundProducts ?
    t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.emptyState.noProductsToRefund')
    :
    allProductsRefunded ?
      t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.emptyState.allProductsRefunded')
      :
      "";
};

export const isAllProductsRefunded = (items: any[]) => {
  return items.every(item => item.is_refunded === "true");
};

export const refundFieldValidations = (value: string, selectedTransaction: any) => {
  const { amount_cents, currency, source_data } = selectedTransaction || {};
  const floatValue = Number(parseFloat((value)));
  const minimumValidAmount = getMinimumValidAmount(currency);
  
  const isInvalidZero = isInvalidZeroValue(value);

  const refundAmountMaximum = convertAmountToCents(maxRefundAmount(selectedTransaction), currency);
  const valueInCents = convertAmountToCents(Number(value), currency);

  const isSouhoola = source_data?.type.toLowerCase().includes("souhoolav3_online");
  const isAmountDiffLessThanSouhoolaMin = ((convertAmountToCents(amount_cents, currency) - Number(value)) < SOUHOOLA_MIN_REFUND_VALUE);

  if (!value) {
    return t('errors.required');
  } else if ((floatValue < minimumValidAmount) || isInvalidZero) {
    return t('errors.enterValidAmount')
  } else if (valueInCents > refundAmountMaximum) {
    return source_data?.type === "MID_TAKSEET" ?
      t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.errors.midTakseetRefundValidation')
      :
      t(`innerLayout.transactionsPage.singleTransactionsPage.modals.refund.errors.${selectedTransaction?.source_data?.instant_refund ? "instantRefundValidation" : "refundValidation"}`);
  } else if (isSouhoola && isAmountDiffLessThanSouhoolaMin) {
    return t('innerLayout.transactionsPage.singleTransactionsPage.modals.refund.errors.souhoolaPartialRefundValidation')
  };
};

export const captureFieldValidations = (value: string, selectedTransaction: any) => {
  const isInvalidZero = isInvalidZeroValue(value);

  const maxCaptureAmount = Number(getMaxCapturedAmount(selectedTransaction));
  const floatValue = Number(parseFloat((value)));
  const minimumValidAmount = getMinimumValidAmount(selectedTransaction?.currency);

  if (!value) {
    return t('errors.required');
  } else if ((floatValue < minimumValidAmount) || isInvalidZero) {
    return t('errors.enterValidAmount')
  } else if (maxCaptureAmount && floatValue > maxCaptureAmount) {
    return t('innerLayout.transactionsPage.singleTransactionsPage.modals.capture.errors.exceedsTheLimit')
  }
};

// export const hasExceededCancelTime = (selectedTransaction: any) => {
//   const currentDate = new Date();
//   const creationDate = new Date(selectedTransaction?.created_at);

//   const msTimeDiff = currentDate.getTime() - creationDate.getTime();
//   const minutesTimeDiff = Math.floor(msTimeDiff / MS_IN_MIN);
//   const hasExceededCancelTime = minutesTimeDiff >= CANCEL_TIME_LIMIT;

//   return hasExceededCancelTime;
// };

export const getMaxCapturedAmount = (selectedTransaction: any) => {
  const { captured_amount, amount_cents, currency } = selectedTransaction || {};
  const totalAmount = convertCentsToMajorUnit(amount_cents, currency);
  const capturedAmount = convertCentsToMajorUnit(captured_amount, currency);
  const maxAmount = convertAmountToFixedDigits(totalAmount - capturedAmount, currency);
  return maxAmount;
};

export const isOtpRequiredRefund = (selectedTransaction: any) => {
  const onlineOtpRefundList = [HALAN];

  const { source_data, integration_type } = selectedTransaction || {};
  const type = source_data?.type;

  const supportsOnlineOtpRefund = onlineOtpRefundList.includes(type) && integration_type === "online";

  return supportsOnlineOtpRefund;
};

export const getTenure = (tenure: any) => {
  if (tenure?.month) {
    return tenure.month;
  } else if (typeof tenure === 'number' || typeof tenure === 'string') {
    return String(tenure);
  } else {
    return undefined;
  }
};

export const submitOTPPathes = {
  halan: "halan/confirm_refund_otp",
  AMANV3: "aman_v3/refund"
};


export const getCustomerNameFields = (src: { name?: string; last_name?: string; first_name?: string }) => {
  const LOCALIZATION_KEY = 'innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer';
  const hasFullName = !!src?.name;
  const hasFirstOrLastName = !!src?.first_name || src?.last_name;

  if (hasFullName) {
    return [
      {
        label: t(`${LOCALIZATION_KEY}.fullName`),
        value: src.name,
      },
    ];
  }

  if (hasFirstOrLastName) {
    return [
      {
        label: t(`${LOCALIZATION_KEY}.firstName`),
        value: src?.first_name || EMPTY_VALUE,
      },
      {
        label: t(`${LOCALIZATION_KEY}.lastName`),
        value: src?.last_name || EMPTY_VALUE,
      },
    ];
  }

  return [
    {
      label: t(`${LOCALIZATION_KEY}.fullName`),
      value: EMPTY_VALUE,
    },
  ];
};

export const isExternallyInitiatedRefund = (
  success: boolean,
  pending: boolean,
  paymentType: string
): boolean =>
  success === false &&
  pending === true &&
  EXTERNAL_REFUND_METHODS.includes(paymentType);